import React, { useState, useContext, useEffect } from 'react';
import { StorageContext } from '../../contexts/StorageContext';
import { ProjectContext } from '../../contexts/ProjectContext';
import PromptPanel from '../PromptPanel';
import ResponsePanel from '../ResponsePanel';
import PromptGenerator from '../../services/PromptGenerator';
import ResponseProcessor from '../../services/ResponseProcessor';
import './PlanningPhase.css';

/**
 * Planning phase component
 * Allows defining project requirements and generating components
 */
function PlanningPhase() {
  const { storage } = useContext(StorageContext);
  const { project, updateProject } = useContext(ProjectContext);
  const [requirements, setRequirements] = useState('');
  const [prompt, setPrompt] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [promptGenerator] = useState(new PromptGenerator());
  const [responseProcessor] = useState(new ResponseProcessor(storage));
  
  // Load saved requirements
  useEffect(() => {
    if (project && project.requirements) {
      setRequirements(project.requirements);
    }
  }, [project]);
  
  // Save requirements
  const handleRequirementsChange = async (e) => {
    const newRequirements = e.target.value;
    setRequirements(newRequirements);
    
    try {
      await storage.updateProject({
        ...project,
        requirements: newRequirements,
        updated: Date.now()
      });
    } catch (error) {
      console.error('Error saving requirements:', error);
    }
  };
  
  // Auto-save requirements when typing stops
  const debouncedSave = React.useCallback(
    debounce(async (value) => {
      try {
        await storage.updateProject({
          ...project,
          requirements: value,
          updated: Date.now()
        });
      } catch (error) {
        console.error('Error auto-saving requirements:', error);
      }
    }, 1000),
    [project, storage]
  );
  
  // Call debounced save when requirements change
  useEffect(() => {
    if (project && requirements !== project.requirements) {
      debouncedSave(requirements);
    }
  }, [requirements, project, debouncedSave]);
  
  // Generate planning prompt
  const handleGeneratePrompt = async () => {
    if (!requirements.trim()) {
      alert('Please enter project requirements before generating a prompt');
      return;
    }
    
    try {
      setIsGeneratingPrompt(true);
      const generatedPrompt = await promptGenerator.generatePlanningPrompt(requirements);
      setPrompt(generatedPrompt);
    } catch (error) {
      console.error('Error generating prompt:', error);
      alert('Failed to generate prompt');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };
  
  // Process AI response
  const handleProcessResponse = async (response) => {
    try {
      setIsProcessingResponse(true);
      
      const result = await responseProcessor.processResponse(
        response,
        'PLANNING',
        project.id
      );
      
      if (result.success) {
        // Update project with phase completion
        const updatedProject = {
          ...project,
          planningComplete: true,
          updated: Date.now()
        };
        
        await storage.updateProject(updatedProject);
        updateProject(updatedProject);
        
        // Store processing results
        setProcessingResults(result);
        
        return { success: true };
      } else {
        return { 
          success: false,
          error: result.error || 'Failed to process response'
        };
      }
    } catch (error) {
      console.error('Error processing response:', error);
      return { 
        success: false,
        error: 'Error processing response: ' + error.message 
      };
    } finally {
      setIsProcessingResponse(false);
    }
  };
  
  return (
    <div className="planning-phase">
      <div className="phase-description">
        <h2>Planning Phase</h2>
        <p>
          In this phase, you'll define your project requirements and have the AI break them down into 
          components, files, and implementation sequence.
        </p>
      </div>
      
      <div className="requirements-section">
        <h3>Project Requirements</h3>
        <p className="instructions">
          Define the requirements for your project in detail. Be specific about features, 
          technologies, and any constraints or preferences.
        </p>
        <textarea 
          value={requirements}
          onChange={handleRequirementsChange}
          placeholder="Enter your project requirements here..."
          className="requirements-input"
        />
      </div>
      
      <div className="ai-interaction">
        <PromptPanel 
          prompt={prompt}
          onGeneratePrompt={handleGeneratePrompt}
          isLoading={isGeneratingPrompt}
          title="Planning Prompt"
        />
        
        <ResponsePanel 
          onProcessResponse={handleProcessResponse}
          isProcessing={isProcessingResponse}
          processingResults={processingResults}
          title="Planning Response"
        />
      </div>
    </div>
  );
}

// Debounce helper function
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export default PlanningPhase;
